import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckIcon                  from '@mui/icons-material/Check';
import CloseIcon                  from '@mui/icons-material/Close';
import CompareArrowsIcon          from '@mui/icons-material/CompareArrows';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import { getInbox, getApprovalById, approveApproval, rejectApproval } from '../../api/approvalApi';
import { useApproval }    from '../../context/ApprovalContext';
import { useToast }       from '../../context/ToastContext';
import { signalRService } from '../../services/signalRService';
import { SIGNALR_EVENTS } from '../../constants/signalREvents';
import { getSectionStyle } from '../../utils/contentTypeStyles';
import ContentComparisonView from './ContentComparisonView';
import RejectModal           from './RejectModal';
import ActionTypeChip, { splitConvertContentName } from './ActionTypeChip';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  }).format(new Date(dateStr));
}

function parseSnapshot(raw) {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
}


const HEAD_COLS = ['Section', 'Content Name', 'Type', 'Submitted By', 'Submitted', 'Actions'];

export default function ApprovalInboxTable({ highlightApprovalId } = {}) {
  const { setInboxCount, recordDecision } = useApproval();
  const { toast }                        = useToast();

  const rowRefs = useRef({});
  const [highlightedId, setHighlightedId] = useState(null);
  const [items,             setItems]             = useState([]);
  const [tableLoading,      setTableLoading]      = useState(true);
  const [openingId,         setOpeningId]         = useState(null);
  const [approvingId,       setApprovingId]       = useState(null);
  const [selected,          setSelected]          = useState(null);
  const [currentSnapshot,   setCurrentSnapshot]   = useState(null);
  const [proposedSnapshot,  setProposedSnapshot]  = useState(null);
  const [proposedData,      setProposedData]      = useState(null);
  const [compareOpen,       setCompareOpen]       = useState(false);
  const [rejectOpen,        setRejectOpen]        = useState(false);
  const [rejectFromCompare, setRejectFromCompare] = useState(false);
  const [actionLoading,     setActionLoading]     = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getInbox();
      setItems(data);
      setInboxCount(data.length);
    } finally {
      setTableLoading(false);
    }
  }, [setInboxCount]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    signalRService.on(SIGNALR_EVENTS.APPROVAL_REQUESTED, load);
    return () => signalRService.off(SIGNALR_EVENTS.APPROVAL_REQUESTED, load);
  }, [load]);

  useEffect(() => {
    signalRService.on(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, load);
    return () => signalRService.off(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, load);
  }, [load]);

  useEffect(() => {
    if (!highlightApprovalId || items.length === 0) return;
    const el = rowRefs.current[highlightApprovalId];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedId(highlightApprovalId);
    const timer = setTimeout(() => setHighlightedId(null), 2500);
    return () => clearTimeout(timer);
  }, [highlightApprovalId, items]);

  const handleCompare = async (item) => {
    setOpeningId(item.approvalId);
    setSelected(item);
    try {
      const full = await getApprovalById(item.approvalId);
      setCurrentSnapshot(parseSnapshot(full?.currentSnapshot));
      setProposedSnapshot(parseSnapshot(full?.proposedSnapshot));
      setProposedData(full?.proposedData ?? null);
      setCompareOpen(true);
    } catch {
      setCurrentSnapshot(null);
      setProposedSnapshot(null);
      setProposedData(null);
      setCompareOpen(true);
    } finally {
      setOpeningId(null);
    }
  };

  const handleCompareClose = () => {
    setCompareOpen(false);
    setSelected(null);
    setCurrentSnapshot(null);
    setProposedSnapshot(null);
    setProposedData(null);
  };

  const handleApprove = async (item) => {
    const target = item ?? selected;
    if (!target) return;
    if (item) setApprovingId(target.approvalId);
    else      setActionLoading(true);
    try {
      await approveApproval(target.approvalId);
      recordDecision(target.approvalId);
      toast.success(`${target.sectionLabel} approved!`);
      handleCompareClose();
      load();
    } finally {
      setApprovingId(null);
      setActionLoading(false);
    }
  };

  const handleRejectOpen = (item) => {
    const fromCompare = !item;
    if (item) setSelected(item);
    setRejectFromCompare(fromCompare);
    setCompareOpen(false);
    setRejectOpen(true);
  };

  const handleReject = async (reason) => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await rejectApproval(selected.approvalId, reason);
      recordDecision(selected.approvalId);
      toast.success(`${selected.sectionLabel} rejected.`);
      setRejectOpen(false);
      handleCompareClose();
      load();
    } finally {
      setActionLoading(false);
    }
  };

  if (tableLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
        <CircularProgress size={36} sx={{ color: '#0a326d' }} />
        <Typography variant="body2" sx={{ color: '#64748b' }}>Loading approvals…</Typography>
      </Box>
    );
  }

  if (!items.length) {
    return (
      <Box
        sx={{
          py: 8, px: 3,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
          bgcolor: 'white', borderRadius: '12px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
        }}
      >
        <Box
          sx={{
            width: 60, height: 60, borderRadius: '16px',
            background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <HourglassEmptyOutlinedIcon sx={{ fontSize: 28, color: '#94a3b8' }} />
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography fontWeight={700} sx={{ color: '#1e293b', mb: 0.5 }}>Nothing pending</Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            No items need your approval right now
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 8px rgba(0,0,0,0.05)',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 700 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              {HEAD_COLS.map((h, i) => (
                <TableCell
                  key={h}
                  align={i === HEAD_COLS.length - 1 ? 'right' : 'left'}
                  sx={{
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    color: '#475569',
                    textTransform: 'uppercase',
                    letterSpacing: '0.6px',
                    py: 1.5,
                    px: 2,
                    borderBottom: '1px solid #e2e8f0',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => {
              const isOpening   = openingId   === item.approvalId;
              const isApproving = approvingId === item.approvalId;
              const isBusy      = isOpening || isApproving;
              const sStyle      = getSectionStyle(item.sectionLabel);
              const { baseName, targetLabel } = splitConvertContentName(item.contentName, item.actionType);

              return (
                <TableRow
                  key={item.approvalId}
                  ref={(el) => { rowRefs.current[item.approvalId] = el; }}
                  sx={{
                    bgcolor: highlightedId === item.approvalId ? '#fef3c7' : 'white',
                    transition: 'background 0.6s ease',
                    '&:hover': { bgcolor: highlightedId === item.approvalId ? '#fef3c7' : '#f8fafc' },
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  {/* Section */}
                  <TableCell sx={{ py: 1.5, px: 2 }}>
                    <Box
                      sx={{
                        display: 'inline-flex', alignItems: 'center',
                        px: 1.25, py: 0.45,
                        borderRadius: '6px',
                        bgcolor: sStyle.bg,
                        border: `1px solid ${sStyle.border}`,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: sStyle.color, lineHeight: 1, whiteSpace: 'nowrap' }}>
                        {item.sectionLabel}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Content Name */}
                  <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8125rem', color: '#1e293b', fontWeight: 500 }}>
                    {baseName || '—'}
                  </TableCell>

                  {/* Type */}
                  <TableCell sx={{ py: 1.5, px: 2 }}>
                    <ActionTypeChip actionType={item.actionType} targetLabel={targetLabel} />
                  </TableCell>

                  {/* Submitted By */}
                  <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8125rem', color: '#475569' }}>
                    {item.submittedByName}
                  </TableCell>

                  {/* Submitted date */}
                  <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8125rem', color: '#475569', whiteSpace: 'nowrap' }}>
                    {formatDate(item.submittedAt)}
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="right" sx={{ py: 1.5, px: 2 }}>
                    <Stack direction="row" spacing={0.75} justifyContent="flex-end" sx={{ whiteSpace: 'nowrap', flexWrap: 'nowrap' }}>
                      <Button
                        size="small"
                        startIcon={isOpening
                          ? <CircularProgress size={12} sx={{ color: '#1d4ed8' }} />
                          : <CompareArrowsIcon sx={{ fontSize: 14 }} />}
                        onClick={() => handleCompare(item)}
                        disabled={isBusy}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          borderRadius: '8px',
                          border: '1px solid #bfdbfe',
                          color: '#1d4ed8',
                          bgcolor: '#eff6ff',
                          px: 1.5,
                          py: 0.5,
                          minWidth: 0,
                          whiteSpace: 'nowrap',
                          '&:hover': { bgcolor: '#dbeafe', borderColor: '#93c5fd' },
                          '&:disabled': { opacity: 0.5 },
                        }}
                      >
                        {isOpening ? 'Loading…' : 'Compare'}
                      </Button>

                      <Button
                        size="small"
                        startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                        onClick={() => handleRejectOpen(item)}
                        disabled={isBusy}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          borderRadius: '8px',
                          border: '1px solid #fecaca',
                          color: '#dc2626',
                          bgcolor: '#fef2f2',
                          px: 1.5,
                          py: 0.5,
                          minWidth: 0,
                          whiteSpace: 'nowrap',
                          '&:hover': { bgcolor: '#fee2e2', borderColor: '#fca5a5' },
                          '&:disabled': { opacity: 0.5 },
                        }}
                      >
                        Reject
                      </Button>

                      <Button
                        size="small"
                        startIcon={isApproving
                          ? <CircularProgress size={12} sx={{ color: 'white' }} />
                          : <CheckIcon sx={{ fontSize: 14 }} />}
                        onClick={() => handleApprove(item)}
                        disabled={isBusy}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          borderRadius: '8px',
                          border: '1px solid transparent',
                          color: 'white',
                          bgcolor: '#16a34a',
                          px: 1.5,
                          py: 0.5,
                          minWidth: 0,
                          whiteSpace: 'nowrap',
                          boxShadow: '0 1px 4px rgba(22,163,74,0.3)',
                          '&:hover': { bgcolor: '#15803d' },
                          '&:disabled': { opacity: 0.5 },
                        }}
                      >
                        {isApproving ? 'Approving…' : 'Approve'}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      </Paper>

      <ContentComparisonView
        open={compareOpen}
        onClose={handleCompareClose}
        contentType={selected?.contentType}
        sectionLabel={selected?.sectionLabel}
        contentName={selected?.contentName}
        actionType={selected?.actionType}
        currentSnapshot={currentSnapshot}
        proposedSnapshot={proposedSnapshot}
        proposedData={proposedData}
        onApprove={() => handleApprove()}
        onReject={() => handleRejectOpen()}
        loading={actionLoading}
      />

      <RejectModal
        open={rejectOpen}
        onClose={() => {
          setRejectOpen(false);
          if (rejectFromCompare) setCompareOpen(true);
        }}
        onConfirm={handleReject}
      />
    </>
  );
}
